// client/src/routes/app/reports/dosar-fiscal.tsx — SCR-REPT-003 „Dosar fiscal anual" · REPORT-003
// (04 §7, §8.14).
//
// A LITERAL SEGMENT BESIDE A PARAMETER, and the router resolves it in that order: `dosar-fiscal`
// is not a report code and must never be matched by `$reportCode`. 04 §7's own route tree lists
// both files, and `NotificationDeepLinks.FiscalDossier` is this exact path — the twelfth
// notification family's deep link, which fell through the client's stored-path branch until this
// route existed (D-1458).
//
// `Fiscal.Export` is the gate: 03 §17.1 grants it to `owner` and `accountant` and withholds it
// from `manager`, all four dossier routes mount it (D-1459), and 04 §8.14 says the screen is
// „visible to `owner` and `accountant`". The gallery mirrors it by not drawing the card; this
// renders SCR-SYS-403 for the URL that was typed anyway (§9.2.2).

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';

import { RequirePermission } from '@/components/layout/route-guards';
import { fiscalVisible } from '@/config/fiscal';
import { DossierWizard } from '@/features/reports/DossierWizard';
import { dossierSearchSchema, type DossierSearch } from '@/features/reports/search';

export const Route = createFileRoute('/app/reports/dosar-fiscal')({
  // Withdrawn from view with the fiscal module (D-2780): the address a FISCAL_DOSSIER
  // notification carries lands on „Bilanț"'s „Rapoarte" tab instead (D-2810).
  beforeLoad: () => {
    // To „Bilanț"'s „Rapoarte" tab rather than to the gallery, which is off the sidebar too
    // (D-2810): the tab is where the reports are exported now.
    if (!fiscalVisible()) throw redirect({ to: '/app/bilant', search: { tab: 'rapoarte' } });
  },
  validateSearch: dossierSearchSchema,
  component: DossierRoute,
});

function DossierRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // One instant per mount: „ultimul an încheiat" must not move under the user mid-form.
  const now = useMemo(() => new Date(), []);

  return (
    <RequirePermission permission="Fiscal.Export">
      <DossierWizard
        search={search}
        now={now}
        onSearchChange={(next: (previous: DossierSearch) => DossierSearch) => {
          void navigate({ search: next });
        }}
      />
    </RequirePermission>
  );
}
